import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import { avisar } from '@xiltrion/ui/sonner';
import { useSession } from '@/app/session';
import { citizenKeys, updateCitizen } from './api';

const ON = 'griddy-icons:label-important-filled', OFF = 'griddy-icons:label-important';

/**
 * Marca de importante de un registro del Directorio. Es de la persona, no de cada radicado: todas las peticiones que
 * llegan de ella (su correo) la muestran. Quien no puede corregir fichas solo la ve. En una fila clicable no abre la ficha.
 */
export function ImportantToggle({ citizen, size = 'icon-sm' }: { citizen: { id: string; fullName: string; important?: boolean }; size?: 'icon-sm' | 'icon' }) {
  const qc = useQueryClient();
  const canEdit = useSession().can('case.create');
  const on = !!citizen.important;
  const toggle = useMutation({
    mutationFn: () => updateCitizen(citizen.id, { important: !on }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: citizenKeys.all }); void qc.invalidateQueries({ queryKey: ['cases'] }); },
    onError: () => avisar.error('No pudimos guardar la marca de importante', { description: 'Intenta de nuevo.' }),
  });
  if (!canEdit) return on ? <ImportantIcon /> : null;
  const text = on ? `Quitar a ${citizen.fullName} de importantes` : `Marcar a ${citizen.fullName} como importante`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="ghost" size={size} aria-pressed={on} aria-label={text} disabled={toggle.isPending}
          className={on ? 'text-primary hover:text-primary' : 'text-muted-foreground hover:text-foreground'}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggle.mutate(); }}
          onKeyDown={(e) => e.stopPropagation()}>
          <Icono nombre={on ? ON : OFF} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{on ? 'Quitar de importantes' : 'Marcar como importante'}</TooltipContent>
    </Tooltip>
  );
}

/** Solo lectura, junto al peticionario de un radicado: la petición viene de alguien marcado como importante. */
export function ImportantIcon() {
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-flex text-primary"><Icono nombre={ON} etiqueta="Importante" /></span></TooltipTrigger>
      <TooltipContent side="bottom">Importante (Directorio)</TooltipContent>
    </Tooltip>
  );
}
